import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import GooglePlaceSearch from "@/components/GooglePlaceSearch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Building2, Plus, MapPin, Radar, Users, Store, CheckCircle2, ScanLine } from "lucide-react";

const EMPTY = { name: "", home_base: null, first_user: { email: "", password: "", name: "" } };

export default function OrganisationsPanel() {
  const [orgs, setOrgs] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = () => api.get("/organisations").then((r) => setOrgs(r.data)).catch(() => toast.error("Could not load organisations"));
  useEffect(() => { load(); }, []);

  const pickHome = (place) => {
    if (place?.latitude == null || place?.longitude == null) { toast.error("That place has no location — pick another"); return; }
    setForm((f) => ({ ...f, home_base: {
      name: place.name, latitude: place.latitude, longitude: place.longitude,
      address: place.address, place_id: place.google_place_id,
    } }));
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Enter an organisation name");
    if (!form.home_base) return toast.error("Pick a home base on Google");
    if (!form.first_user.email.trim() || form.first_user.password.length < 6 || !form.first_user.name.trim())
      return toast.error("Enter the first user's name, email and a password (6+ characters)");
    setBusy(true);
    try {
      const { data } = await api.post("/organisations", form);
      toast.success(`Created ${data.name} — ${data.prospects_copied.toLocaleString()} prospects copied`);
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleScanning = async (org) => {
    setTogglingId(org.id);
    try {
      await api.patch(`/organisations/${org.id}`, { scanning_enabled: !org.scanning_enabled });
      toast.success(`Scanning ${!org.scanning_enabled ? "enabled" : "disabled"} for ${org.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTogglingId(null); }
  };

  if (!orgs) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" /></div>;

  return (
    <div className="space-y-5" data-testid="organisations-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-black text-[#1E3A8A] flex items-center gap-2"><Building2 className="w-6 h-6" /> Organisations</h2>
          <p className="text-sm text-[#6B7280] mt-1">Each organisation has its own prospects, customers and data — fully separate.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-org-btn"
          className="h-11 px-4 rounded-lg bg-[#1E3A8A] text-white font-bold text-sm flex items-center gap-2 shrink-0 whitespace-nowrap">
          <Plus className="w-4 h-4" /> New organisation
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {orgs.map((o) => (
          <div key={o.id} className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid={`org-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-black text-[#111827] truncate flex items-center gap-2">
                  {o.name}
                  {o.is_default && <span className="text-[10px] font-bold uppercase bg-[#EEF2FF] text-[#1E3A8A] px-2 py-0.5 rounded-full">Owner</span>}
                </div>
                {o.home_base?.address && <div className="text-xs text-[#6B7280] flex items-center gap-1 mt-0.5 truncate"><MapPin className="w-3.5 h-3.5 shrink-0" /> {o.home_base.address}</div>}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-[#374151]">
              <span className="flex items-center gap-1.5"><Radar className="w-4 h-4 text-[#9CA3AF]" /> {o.garages.toLocaleString()} prospects</span>
              <span className="flex items-center gap-1.5"><Store className="w-4 h-4 text-[#9CA3AF]" /> {o.customers.toLocaleString()} customers</span>
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#9CA3AF]" /> {o.users} users</span>
            </div>
            <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center justify-between">
              <span className="text-sm font-bold flex items-center gap-1.5">
                <ScanLine className="w-4 h-4 text-[#9CA3AF]" /> Area scanning
                <span className={o.scanning_enabled ? "text-[#047857]" : "text-[#9CA3AF]"}>{o.scanning_enabled ? "On" : "Off"}</span>
              </span>
              <button onClick={() => toggleScanning(o)} disabled={togglingId === o.id} data-testid={`org-scan-toggle-${o.id}`}
                className={`h-9 px-3 rounded-md font-bold text-sm flex items-center gap-1.5 disabled:opacity-60 ${o.scanning_enabled ? "border border-[#D1D5DB] text-[#B91C1C]" : "bg-[#047857] text-white"}`}>
                {togglingId === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : o.scanning_enabled ? "Turn off" : "Turn on"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-org-dialog">
          <DialogHeader><DialogTitle className="text-xl font-black text-[#1E3A8A]">New organisation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Organisation name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name-input"
                placeholder="e.g. Agastya Outdoor Photography"
                className="w-full h-11 px-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Home base (search Google)</label>
              <GooglePlaceSearch onSelect={pickHome} placeholder="Search their shop / base on Google…" />
              {form.home_base && (
                <div className="mt-2 text-sm text-[#047857] font-bold flex items-center gap-1.5" data-testid="org-home-picked">
                  <CheckCircle2 className="w-4 h-4" /> {form.home_base.name || "Location"} — {form.home_base.address}
                </div>
              )}
            </div>
            <div className="pt-2 border-t border-[#F3F4F6]">
              <div className="text-xs font-bold uppercase text-[#6B7280] mb-2">First user (manager of this organisation)</div>
              <div className="space-y-2">
                <input value={form.first_user.name} onChange={(e) => setForm({ ...form, first_user: { ...form.first_user, name: e.target.value } })} data-testid="org-user-name"
                  placeholder="Full name" className="w-full h-11 px-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
                <input value={form.first_user.email} onChange={(e) => setForm({ ...form, first_user: { ...form.first_user, email: e.target.value } })} data-testid="org-user-email"
                  placeholder="Email" type="email" className="w-full h-11 px-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
                <input value={form.first_user.password} onChange={(e) => setForm({ ...form, first_user: { ...form.first_user, password: e.target.value } })} data-testid="org-user-password"
                  placeholder="Password (6+ characters)" type="text" className="w-full h-11 px-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
              </div>
            </div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 text-xs text-[#6B7280]">
              A full copy of the prospect database is created for this organisation. Area scanning stays <b>off</b> until you turn it on.
            </div>
            <button onClick={create} disabled={busy} data-testid="org-create-submit"
              className="w-full h-12 rounded-md bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating & copying prospects…</> : <><Building2 className="w-5 h-5" /> Create organisation</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
